import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { navigateToVmwareHorizonApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { AppType } from "client/Api/Apps/common";
import { TileRenderState } from "../RenderState/common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type VmwareHorizonAppTileContentProps = {
	renderState: TileRenderState<AppType.VmwareHorizonApp>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const VmwareHorizonAppTileContent = (props: VmwareHorizonAppTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useVmwareHorizonAppTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
VmwareHorizonAppTileContent.displayName = "VmwareHorizonAppTileContent";

export function useVmwareHorizonAppTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.VmwareHorizonApp>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: {
			destinationWindow
		},
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						registerTileUsage();

						navigateToVmwareHorizonApp({
							id: environmentAppId,
							destinationWindow
						});
					},
					[
						destinationWindow,
						environmentAppId,
						registerTileUsage
					]
				)
			)
		)
	);
}
